// src/registry.ts
var CONTEXT_REGISTRY = {
  scheme: { pathKey: "schemes", prefixes: ["schemes"], reachable: true },
  ledger: { pathKey: "ledger", prefixes: ["ledger"], reachable: true },
  redemption: { pathKey: "redemptions", prefixes: ["redemptions"], reachable: true },
  loan: { pathKey: "loan-leads", prefixes: ["loan-leads"], reachable: true },
  goals: { pathKey: "goals", prefixes: ["goals"], reachable: true },
  network: { pathKey: "network", prefixes: ["network"], reachable: true },
  audit: { pathKey: "audit", prefixes: ["audit"], reachable: true },
  comms: {
    pathKey: "comms",
    // `announcements` is comms-service's only reader-facing prefix — everything
    // else here is SQS-in or staff-read-only. It sits at the root rather than
    // under `notifications` because `AnnouncementsController` is mapped to
    // `/announcements`, not nested under the notification surface.
    prefixes: [
      "notifications",
      "notification-preferences",
      "templates",
      "channel-providers",
      "announcements"
    ],
    reachable: true
  },
  helpdesk: { pathKey: "helpdesk", prefixes: ["helpdesk"], reachable: true },
  identity: {
    pathKey: "identity",
    prefixes: ["kyc", "me", "users"],
    publicPrefixes: ["auth"],
    reachable: true
  },
  payment: { pathKey: "payments", prefixes: ["payments"], reachable: true },
  compliance: { pathKey: "compliance", prefixes: ["compliance"], reachable: true },
  dispute: { pathKey: "disputes", prefixes: ["disputes"], reachable: true },
  reporting: { pathKey: "reports", prefixes: ["reports"], reachable: true },
  tenant: { pathKey: "tenants", prefixes: ["tenants"], reachable: true },
  pricing: {
    pathKey: "pricing",
    prefixes: ["pricing"],
    publicPrefixes: ["pricing/current"],
    reachable: true
  },
  // Controllers exist (/partners, /partner, /commission-plan, /lenders) but no
  // gateway stack routes them (§16 B5). Marked unreachable so adoption fails loudly.
  partner: {
    pathKey: "partners",
    prefixes: ["partners", "partner", "commission-plan"],
    reachable: false
  },
  lender: { pathKey: "lenders", prefixes: ["lenders"], reachable: false },
  // crm-service is designed but not yet built (docs/frontend/crm-mfe-design.md §5).
  // The CRM MFE runs against @quickbee/api-crm fixtures; flip `reachable` in the
  // change that deploys the service's gateway stack.
  crm: { pathKey: "crm", prefixes: ["crm"], reachable: false },
  // campaign-service is designed but not yet built
  // (docs/frontend/campaign-mfe-design.md §5). The Campaign MFE runs against
  // @quickbee/api-campaign fixtures; flip `reachable` in the change that
  // deploys the service's gateway stack.
  campaign: { pathKey: "campaign", prefixes: ["campaign"], reachable: false }
};
var CONTEXT_KEYS = Object.keys(CONTEXT_REGISTRY);
function getContextDescriptor(context) {
  const descriptor = CONTEXT_REGISTRY[context];
  if (!descriptor) {
    throw new Error(`[api-config] Unknown bounded context "${context}".`);
  }
  return descriptor;
}
function isPublicPath(context, path) {
  const publicPrefixes = getContextDescriptor(context).publicPrefixes;
  if (!publicPrefixes) return false;
  const normalised = path.replace(/^\/+/, "");
  return publicPrefixes.some(
    (prefix) => normalised === prefix || normalised.startsWith(`${prefix}/`)
  );
}
function isKnownPrefix(context, path) {
  const descriptor = getContextDescriptor(context);
  const normalised = path.replace(/^\/+/, "");
  const all = [...descriptor.prefixes, ...descriptor.publicPrefixes ?? []];
  return all.some((prefix) => normalised === prefix || normalised.startsWith(`${prefix}/`));
}

// src/errors.ts
var UnreachableContextError = class extends Error {
  context;
  constructor(context) {
    super(
      `[api-config] Bounded context "${context}" has no deployed API Gateway stack (CONTEXT_REGISTRY.${context}.reachable === false). Screens backed by this context must stay on fixtures until the gateway ships \u2014 see backend-api-integration-design.md \xA716 B5.`
    );
    this.name = "UnreachableContextError";
    this.context = context;
  }
};
var RuntimeConfigError = class extends Error {
  constructor(message) {
    super(`[api-config] ${message}`);
    this.name = "RuntimeConfigError";
  }
};

// src/runtimeConfig.ts
var SUPPORTED_CONFIG_VERSION = 1;
var LEGACY_KEYS = ["apiUrl", "apiBaseUrl"];
var OFFLINE_CONFIG = {
  apiOrigin: "",
  cdnOrigin: "",
  env: "development",
  configVersion: SUPPORTED_CONFIG_VERSION,
  apiMode: "mock"
};
var cached = null;
var warnedOffline = false;
function isRecord(value) {
  return typeof value === "object" && value !== null;
}
function trimTrailingSlash(value) {
  return value.replace(/\/+$/, "");
}
function assertIsBareOrigin(apiOrigin) {
  let parsed;
  try {
    parsed = new URL(apiOrigin);
  } catch {
    throw new RuntimeConfigError(
      `window.__QB_CONFIG.apiOrigin "${apiOrigin}" is not an absolute URL (expected e.g. "https://api.thequickbee.com" or "http://localhost:8080").`
    );
  }
  if (parsed.pathname !== "/" && parsed.pathname !== "") {
    throw new RuntimeConfigError(
      `window.__QB_CONFIG.apiOrigin must be an origin only ("https://api.example.com"), not a path \u2014 received "${apiOrigin}". The /{pathKey}/api/v1 prefix is owned by CONTEXT_REGISTRY.`
    );
  }
}
function normaliseRuntimeConfig(raw) {
  if (!isRecord(raw)) {
    throw new RuntimeConfigError("window.__QB_CONFIG is missing or not an object.");
  }
  for (const legacy of LEGACY_KEYS) {
    if (legacy in raw) {
      throw new RuntimeConfigError(
        `window.__QB_CONFIG carries the removed key "${legacy}". The only supported name is "apiOrigin" (scheme + host, no path). Rebuild/redeploy the shell \u2014 \xA75.4 rule 1.`
      );
    }
  }
  const version = raw["configVersion"];
  if (version !== SUPPORTED_CONFIG_VERSION) {
    throw new RuntimeConfigError(
      `Unsupported window.__QB_CONFIG.configVersion ${String(version)} (this build understands ${SUPPORTED_CONFIG_VERSION}).`
    );
  }
  const apiOrigin = typeof raw["apiOrigin"] === "string" ? trimTrailingSlash(raw["apiOrigin"]) : "";
  if (apiOrigin) assertIsBareOrigin(apiOrigin);
  const env = typeof raw["env"] === "string" ? raw["env"] : "development";
  const explicitMode = raw["apiMode"];
  const apiMode = explicitMode === "mock" || explicitMode === "live" ? explicitMode : apiOrigin ? "live" : "mock";
  const config = {
    apiOrigin,
    cdnOrigin: typeof raw["cdnOrigin"] === "string" ? trimTrailingSlash(raw["cdnOrigin"]) : "",
    env,
    configVersion: SUPPORTED_CONFIG_VERSION,
    apiMode
  };
  if (raw["authDisabled"] === true) config.authDisabled = true;
  if (isRecord(raw["devSession"])) config.devSession = raw["devSession"];
  if (typeof raw["useGatewayPathKey"] === "boolean") {
    config.useGatewayPathKey = raw["useGatewayPathKey"];
  }
  return config;
}
function getRuntimeConfig() {
  if (cached) return cached;
  if (typeof window === "undefined") {
    return OFFLINE_CONFIG;
  }
  const raw = window.__QB_CONFIG;
  if (raw === void 0) {
    if (!warnedOffline) {
      warnedOffline = true;
      console.warn(
        "[api-config] window.__QB_CONFIG is not set \u2014 running in offline/mock mode. Set QB_API_URL in apps/shell/.env.local to talk to a real backend."
      );
    }
    cached = OFFLINE_CONFIG;
    return cached;
  }
  cached = normaliseRuntimeConfig(raw);
  return cached;
}
function setRuntimeConfigForTesting(config) {
  cached = config;
  warnedOffline = false;
}
function getApiMode() {
  return getRuntimeConfig().apiMode ?? "live";
}
function isOffline() {
  const config = getRuntimeConfig();
  return !config.apiOrigin;
}
function isLocalEnv(env) {
  return env === "development" || env === "dev";
}
function isAuthDisabled() {
  const config = getRuntimeConfig();
  return config.authDisabled === true && isLocalEnv(config.env);
}
function getDevSession() {
  if (!isAuthDisabled()) return null;
  return getRuntimeConfig().devSession ?? null;
}
var activeApp = "shell";
function setActiveApp(appName) {
  if (appName) activeApp = appName;
}
function getActiveApp() {
  return activeApp;
}

// src/resolve.ts
var LOCAL_HOSTNAMES = /* @__PURE__ */ new Set(["localhost", "127.0.0.1", "0.0.0.0", "::1", "[::1]"]);
function isLocalOrigin(origin) {
  if (!origin) return false;
  try {
    const { hostname } = new URL(origin);
    return LOCAL_HOSTNAMES.has(hostname) || hostname.endsWith(".localhost");
  } catch {
    return false;
  }
}
function usesGatewayPathKey(config = getRuntimeConfig()) {
  return config.useGatewayPathKey ?? !isLocalOrigin(config.apiOrigin);
}
function resolveContextBaseUrl(context, config = getRuntimeConfig()) {
  const descriptor = getContextDescriptor(context);
  const useKey = usesGatewayPathKey(config);
  if (!descriptor.reachable && useKey) {
    throw new UnreachableContextError(context);
  }
  const origin = config.apiOrigin.replace(/\/+$/, "");
  const key = useKey ? `/${descriptor.pathKey}` : "";
  return `${origin}${key}/api/v1`;
}
function resolveContextUrl(context, path, config = getRuntimeConfig()) {
  const base = resolveContextBaseUrl(context, config);
  return `${base}/${path.replace(/^\/+/, "")}`;
}

export { CONTEXT_KEYS, CONTEXT_REGISTRY, RuntimeConfigError, UnreachableContextError, getActiveApp, getApiMode, getContextDescriptor, getDevSession, getRuntimeConfig, isAuthDisabled, isKnownPrefix, isLocalOrigin, isOffline, isPublicPath, normaliseRuntimeConfig, resolveContextBaseUrl, resolveContextUrl, setActiveApp, setRuntimeConfigForTesting, usesGatewayPathKey };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map